import { Box, Grid, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import isEmpty from "../../../../common/helpers/isEmpty";
import OrderBundleActionRow from "../../OrderBundleActionRow";
import { OrderRow } from "../../orderTypes";
import {
  OrderFormShortRowBundlingContainer,
  OrderFormShortRowBundlingHeader,
} from "../../styles";
import { sortOrderFieldsByDateTime } from "../../utils";
import OrderFormShortRowFields from "./OrderFormShortRowFields";
import { ShiftOrderContext } from "./OrderShortDataProvider";
import * as orderRowHandlers from "./orderFormShortRowHandlers";

type Props = {
  row: OrderRow;
  bundleRowId: string;
};

const OrderFormShortBundlingContainer = ({ row, bundleRowId }: Props) => {
  const { t } = useTranslation(["common, order"]);
  const [isEditing, setIsEditing] = useState(true);
  const {
    handleRemoveBundleContainer,
    resetBundlingState,
    setBundling,
    validationErrors,
  } = useContext(ShiftOrderContext);

  const handleEditingCompleted = () => {
    row.rows.sort(sortOrderFieldsByDateTime);

    setIsEditing(false);

    if (!row.rows.length) {
      handleRemoveBundleContainer(bundleRowId);
    }

    resetBundlingState();
  };

  const handleIsEditingChange = () => {
    setIsEditing(true);

    // init bundle state with the first row values
    setBundling({
      show: true,
      bundleId: bundleRowId,
      bundleTemplate: row.rows[0].orderTemplate.id,
      bundleWorkLocation: row.rows[0].workLocation.id,
      bundleReasonForTemporaryEmployment:
        row.rows[0].reasonForTemporaryEmployment,
      bundleAmount: row.rows[0].amount,
      resetManuallyAddedTalents: false,
      resetTimeBasedNotification: false,
    });
  };

  const bundleRowHasEmptyValues = row.rows.some(
    // destruct optional fields to exclude them from validation
    ({
      descriptionToEezyAndCustomer,
      descriptionToEezyAndCustomerAndTalent,
      automatching,
      isFillShift,
      flexibleStartUntil,
      notifyWhenFulfilled,
      notifyMinutesBeforeIfNotFulfilled,
      ...rest
    }) => {
      return Object.values(rest).some((value) =>
        orderRowHandlers.isFieldValueEmpty(value),
      );
    },
  );

  const currentRowIds = row.rows.map((row) => row.id);
  const includesInvalidRows = currentRowIds.some((id) =>
    validationErrors.find((error) => error.id === id),
  );

  const bundleButtonDisabled =
    bundleRowHasEmptyValues || !isEmpty(includesInvalidRows);

  return (
    <Grid item xs={13} ml={-1}>
      <OrderFormShortRowBundlingContainer>
        <Box
          sx={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}
        >
          <OrderFormShortRowBundlingHeader variant="header">
            {isEditing
              ? t("order:addShiftsToBundle")
              : t(
                  row.rows.length === 1
                    ? "order:oneBundledShift"
                    : "order:bundledShiftCount",
                  { count: row.rows.length },
                )}
          </OrderFormShortRowBundlingHeader>
          {!isEditing && (
            <Typography
              component="button"
              sx={{
                textDecoration: "underline",
                color: "rgba(0, 0, 0, 0.52)",
                textTransform: "capitalize",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
              onClick={handleIsEditingChange}
            >
              {t("common:edit")}
            </Typography>
          )}
        </Box>
        {row.rows.map((rowValues, index) => (
          <Grid
            key={rowValues.id}
            container
            item
            spacing={1}
            alignItems="center"
            wrap="nowrap"
            mt="1px"
          >
            <OrderFormShortRowFields
              rowId={row.rowId}
              values={rowValues}
              isFirstRow={index === 0}
              bundleRowId={bundleRowId}
            />
          </Grid>
        ))}
        <OrderBundleActionRow
          isEditing={isEditing}
          row={row}
          bundleRowId={bundleRowId}
          bundleButtonDisabled={bundleButtonDisabled}
          handleEditingCompleted={handleEditingCompleted}
        />
      </OrderFormShortRowBundlingContainer>
    </Grid>
  );
};

export default OrderFormShortBundlingContainer;
